import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { GiftCardPurchase } from "@/components/GiftCardPurchase";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

function StorefrontArticles({ enhancerId }) {
  const { data: articles = [] } = useQuery({
    queryKey: ["storefront-articles", enhancerId],
    queryFn: () => api.get(`/enhancers/${enhancerId}/articles`).then((r) => r.data),
  });
  if (!articles.length) return null;
  return (
    <section className="mb-10" data-testid="storefront-articles">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Articles</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.slice(0, 6).map((a) => (
          <Link key={a.id} to={`/articles/${a.slug || a.id}`} data-testid={`storefront-article-${a.id}`}
            className="eyw-card overflow-hidden hover:shadow-lg transition-shadow group">
            {a.cover_image && <img src={a.cover_image} alt={a.title} className="h-36 w-full object-cover" />}
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-wide text-[#8FA89B] mb-1">{a.category} · {(a.created_at || "").slice(0, 10)}</p>
              <p className="font-serif text-[#1A242B] leading-snug group-hover:text-[#144F75] transition-colors">{a.title}</p>
              {cleanExcerpt(a, 120) && <p className="text-xs text-[#4A5D6B] mt-1.5 line-clamp-2">{cleanExcerpt(a, 120)}</p>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
import {
  Loader2, ArrowLeft, MapPin, Star, ShoppingBag, Calendar, Download, Package,
  Trash2, MessageSquareQuote, MessageSquare, Globe, Youtube, Instagram, Facebook, PlayCircle, Link2, Store,
  CheckCircle2, Camera, Clock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { cleanExcerpt } from "@/lib/articleHtml";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import BookingModal from "@/components/BookingModal";
import InquiryModal from "@/components/InquiryModal";
import { sanitizeHtml } from "@/components/RichTextEditor";
import CatalogImage from "@/components/CatalogImage";
import StorefrontPackages from "@/components/StorefrontPackages";
import { SocialShare } from "@/components/SocialShare";
import Seo, { stripHtml } from "@/components/Seo";
import { money } from "@/lib/format";
import { rememberReferral } from "@/lib/api";

import RichContent from "@/components/RichContent";
const Stars = ({ value = 0, size = 16, onSelect }) => (
  <span className="inline-flex">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} onClick={onSelect ? () => onSelect(n) : undefined}
        className={`${onSelect ? "cursor-pointer" : ""} ${n <= Math.round(value) ? "fill-[#E0A458] text-[#E0A458]" : "text-[#D8D2C8]"}`} />
    ))}
  </span>
);

const Avatar = ({ name, avatar, size = "md" }) => {
  const cls = size === "lg"
    ? "h-28 w-28 text-5xl ring-4 ring-white shadow-xl"
    : "h-24 w-24 text-4xl";
  return avatar
    ? <img src={avatar} alt={name} className={`rounded-full object-cover shrink-0 ${cls}`} />
    : <span className={`grid place-items-center rounded-full bg-[#144F75]/10 text-[#144F75] font-semibold shrink-0 ${cls}`}>{(name || "?").slice(0, 1).toUpperCase()}</span>;
};

// Inline gallery lightbox — click a photo to view full-size
function GallerySection({ photos }) {
  const [open, setOpen] = useState(null); // index of open photo
  if (!photos?.length) return null;
  return (
    <section className="mb-10" data-testid="storefront-gallery">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-4 flex items-center gap-2">
        <Camera size={20} className="text-[#D98E73]" /> Gallery
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((url, i) => (
          <button key={i} onClick={() => setOpen(i)} data-testid={`gallery-img-${i}`}
            className="aspect-square rounded-xl overflow-hidden bg-[#F4F1EB] group relative">
            <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-xl" />
          </button>
        ))}
      </div>
      {/* Lightbox */}
      {open !== null && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setOpen(null)} data-testid="gallery-lightbox">
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={photos[open]} alt={`Photo ${open + 1}`} className="w-full max-h-[85vh] object-contain rounded-xl" />
            <div className="flex items-center justify-between mt-3">
              <button onClick={() => setOpen((i) => (i - 1 + photos.length) % photos.length)}
                className="text-white/70 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 text-sm">← Prev</button>
              <span className="text-white/60 text-sm">{open + 1} / {photos.length}</span>
              <button onClick={() => setOpen((i) => (i + 1) % photos.length)}
                className="text-white/70 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 text-sm">Next →</button>
            </div>
            <button onClick={() => setOpen(null)} className="absolute -top-10 right-0 text-white/70 hover:text-white text-2xl font-light">✕</button>
          </div>
        </div>
      )}
    </section>
  );
}

const SocialLink = ({ href, icon: Icon, label }) => (
  <a href={href} target="_blank" rel="noreferrer" title={label}
    className="inline-flex items-center gap-1.5 rounded-full border border-[#144F75]/20 px-3 py-1.5 text-xs font-medium text-[#144F75] hover:bg-[#144F75]/[0.06] transition-colors">
    <Icon size={14} /> {label}
  </a>
);


function ReviewComposer({ enhancerId, isStaff }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [featured, setFeatured] = useState(false);
  const [authorName, setAuthorName] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/enhancers/${enhancerId}/reviews`, { rating, comment, featured, author_name: authorName }),
    onSuccess: () => {
      toast.success("Review posted");
      setComment(""); setAuthorName("");
      qc.invalidateQueries({ queryKey: ["enhancer", enhancerId] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail?.message || e.response?.data?.detail)),
  });
  return (
    <div className="eyw-card p-5" data-testid="review-composer">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm font-medium text-[#1A242B]">Your rating</span>
        <Stars value={rating} size={20} onSelect={setRating} />
      </div>
      {isStaff && (
        <label className="flex items-center gap-2 text-sm text-[#4A5D6B] mb-3">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="review-featured" /> Post as a featured testimonial
        </label>
      )}
      {isStaff && featured && (
        <input value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder="Client name (for testimonial)" data-testid="review-author" className="eyw-input w-full mb-3" />
      )}
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your experience…" data-testid="review-comment" className="eyw-input w-full resize-none" />
      <div className="flex justify-end mt-2">
        <button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="review-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">
          {mut.isPending && <Loader2 className="animate-spin" size={15} />} Post review
        </button>
      </div>
    </div>
  );
}

export default function EnhancerStorefront() {
  const { id } = useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [booking, setBooking] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const { data: e, isLoading, error } = useQuery({
    queryKey: ["enhancer", id],
    queryFn: () => api.get(`/enhancers/${id}`).then((r) => r.data),
    retry: false,
  });
  // Canonicalize the address bar to the name-based permalink (soft redirect —
  // old id/legacy-slug links keep working, but visitors see & copy the real permalink).
  useEffect(() => {
    if (e?.slug && id !== e.slug) window.history.replaceState(null, "", `/enhancers/${e.slug}`);
  }, [e?.slug, id]);
  useEffect(() => { if (e?.id) rememberReferral(e.id); }, [e?.id]);
  const { data: centerSvcs = [] } = useQuery({
    queryKey: ["enhancer-center-svcs", e?.id], enabled: !!e?.id,
    queryFn: () => api.get(`/enhancers/${e.id}/center-services`).then((r) => r.data),
  });
  const { data: offerings } = useQuery({
    queryKey: ["enhancer-offerings", id],
    queryFn: () => api.get(`/enhancers/${id}/offerings`).then((r) => r.data.groups),
  });
  const hasCenterOfferings = (offerings || []).some((g) => (g.items || []).some((i) => i.kind === "center"));
  const { data: curatedPackages = [] } = useQuery({
    queryKey: ["curated-packages"],
    queryFn: () => api.get("/packages/catalog").then((r) => r.data.filter((p) => p.owner_type !== "enhancer")),
    enabled: hasCenterOfferings,
  });
  const delReview = useMutation({
    mutationFn: (rid) => api.delete(`/enhancers/${e?.id || id}/reviews/${rid}`),
    onSuccess: () => { toast.success("Review removed"); qc.invalidateQueries({ queryKey: ["enhancer", id] }); },
  });

  if (isLoading) return <div className="grid place-items-center py-40 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>;
  if (error || !e) return <div className="text-center py-40 text-[#4A5D6B]">Enhancer not found. <Link to="/enhancers" className="text-[#144F75]">Browse Enhancers</Link></div>;

  const isStaff = ["admin", "council", "staff"].includes(user?.role);
  const isVendor = Boolean(e?.is_vendor);

  return (
    <div data-testid="enhancer-storefront" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
      <Seo title={e.name} description={stripHtml(e.intro || "", 160) || e.headline || stripHtml(e.bio) || `Explore offerings and book a session with ${e.name} at Enhancing Your World.`}
        image={e.avatar} path={`/enhancers/${e.slug || e.id}`} type="profile"
        jsonLd={{ "@context": "https://schema.org", "@type": "Person", name: e.name,
          jobTitle: e.headline || undefined, image: e.avatar || undefined,
          url: `${window.location.origin}/enhancers/${e.slug || e.id}`,
          knowsAbout: (e.modalities || []).concat(e.focus_areas || []).slice(0, 12),
          aggregateRating: e.review_count > 0 ? {
            "@type": "AggregateRating", ratingValue: e.avg_rating,
            reviewCount: e.review_count, bestRating: 5,
          } : undefined,
          worksFor: { "@type": "Organization", name: "Enhancing Your World" } }} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Link to="/enhancers" className="inline-flex items-center gap-1 text-sm text-[#4A5D6B] hover:text-[#144F75]"><ArrowLeft size={15} /> Enhancers</Link>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button onClick={() => setContactOpen(true)} data-testid="enhancer-contact-btn" className="eyw-btn !py-2 text-sm"><MessageSquareQuote size={15} /> Ask a Question</button>
          {user && user.id !== (e.id || id) && (
            <button onClick={() => nav(`/messages?to=${e.id || id}`)} data-testid="enhancer-message-btn" className="eyw-btn-ghost !py-2 text-sm border border-[#144F75]/30"><MessageSquare size={15} /> Message</button>
          )}
          <SocialShare path={`/enhancers/${e.slug || e.id}`} ogPath={`/api/share/enhancer/${e.slug || e.id}`} title={e.name} testid="enhancer-share" label="Share profile" />
        </div>
      </div>
      <InquiryModal open={contactOpen} onClose={() => setContactOpen(false)} targetType="enhancer" targetId={e.id || id} targetName={e.name} />

      {/* ---- Profile header ---- */}
      {e.cover_banner && (
        <div className="relative h-[180px] sm:h-[260px] -mx-6 lg:-mx-10 overflow-hidden rounded-t-3xl mb-0" data-testid="storefront-cover-banner">
          <img src={e.cover_banner} alt={`${e.name} — cover image`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A242B]/10 to-[#F9F7F3]/80" />
        </div>
      )}

      <div className={`eyw-card ${e.cover_banner ? "rounded-t-none border-t-0 -mt-1" : ""} p-6 sm:p-8 mb-8`}>
        <div className={`flex flex-col sm:flex-row items-start gap-5 sm:gap-7 ${e.cover_banner ? "-mt-16 sm:-mt-20" : ""}`}>
          <Avatar name={e.name} avatar={e.avatar} size={e.cover_banner ? "lg" : "md"} />
          <div className={`flex-1 min-w-0 ${e.cover_banner ? "mt-2 sm:mt-8" : ""}`}>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-serif text-3xl text-[#1A242B]" data-testid="storefront-name">{e.name}</h1>
              {e.years_experience > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#144F75]/8 text-[#144F75] px-2.5 py-1 text-xs font-medium" data-testid="storefront-years-exp">
                  <Clock size={11} /> {e.years_experience} yrs
                </span>
              )}
              {e.is_vendor && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B5794E]/12 text-[#B5794E] px-3 py-1 text-xs font-semibold" data-testid="storefront-vendor-badge">
                  <Store size={13} /> Vendor{e.vendor_business ? ` · ${e.vendor_business}` : ""}
                </span>
              )}
            </div>
            {e.headline && <p className="text-[#144F75] mt-1 text-base leading-snug" data-testid="storefront-headline">{e.headline}</p>}
            {!e.is_vendor && (
              <div className="flex items-center gap-3 mt-2">
                <Stars value={e.avg_rating} />
                <span className="text-sm text-[#4A5D6B]">{e.avg_rating || "—"} · {e.review_count} {e.review_count === 1 ? "review" : "reviews"}</span>
              </div>
            )}
            {e.location && <p className="text-sm text-[#8FA89B] flex items-center gap-1 mt-2"><MapPin size={13} /> {e.location}</p>}
            {/* Specialties */}
            {e.specialties?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="storefront-specialties">
                {e.specialties.map((s) => <span key={s} className="rounded-full bg-[#144F75]/8 text-[#144F75] px-3 py-1 text-xs">{s}</span>)}
              </div>
            )}
            {/* Certifications — verified badges */}
            {e.certifications?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="storefront-certifications">
                {e.certifications.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-[#5C8A72]/10 text-[#3A6B57] border border-[#5C8A72]/20 px-3 py-1 text-xs font-medium">
                    <CheckCircle2 size={11} className="shrink-0" /> {c}
                  </span>
                ))}
              </div>
            )}
            {/* Bio */}
            {e.bio && <RichContent text={e.bio} className="article-html text-[#4A5D6B] leading-relaxed mt-4" testid="storefront-bio" />}
            {!e.bio && e.intro && <p className="text-[#4A5D6B] leading-relaxed mt-4" data-testid="storefront-intro">{e.intro}</p>}
            {/* Social links */}
            {(e.website || Object.values(e.social_links || {}).some(Boolean)) && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="enhancer-social-links">
                {e.website && <SocialLink href={e.website} icon={Globe} label="Website" />}
                {e.social_links?.instagram && <SocialLink href={e.social_links.instagram} icon={Instagram} label="Instagram" />}
                {e.social_links?.facebook && <SocialLink href={e.social_links.facebook} icon={Facebook} label="Facebook" />}
                {e.social_links?.youtube && <SocialLink href={e.social_links.youtube} icon={Youtube} label="YouTube" />}
                {e.social_links?.spotify && <SocialLink href={e.social_links.spotify} icon={PlayCircle} label="Spotify" />}
                {e.social_links?.tiktok && <SocialLink href={e.social_links.tiktok} icon={Link2} label="TikTok" />}
                {e.social_links?.podcast && <SocialLink href={e.social_links.podcast} icon={PlayCircle} label="Podcast" />}
              </div>
            )}
            {/* ── CTA buttons — always visible inside the profile card ─────── */}
            <div className="flex flex-wrap items-center gap-3 mt-5 pt-4 border-t border-[#E5E0D8]">
              <button onClick={() => setContactOpen(true)} data-testid="enhancer-contact-btn-card" className="eyw-btn !py-2.5 text-sm">
                <MessageSquareQuote size={15} /> Ask a Question
              </button>
              {user && user.id !== (e.id || id) && (
                <button onClick={() => nav(`/messages?to=${e.id || id}`)} data-testid="enhancer-message-btn-card" className="eyw-btn-ghost !py-2.5 text-sm border border-[#144F75]/40">
                  <MessageSquare size={15} /> Message
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isVendor && e.media?.length > 0 && (
        <section className="mb-10" data-testid="storefront-media">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Podcasts & Videos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {e.media.map((m) => (
              <a key={m.id} href={m.video_embed_url} target="_blank" rel="noreferrer" data-testid={`storefront-media-${m.id}`} className="eyw-card p-5 flex flex-col hover:shadow-md transition-shadow">
                {m.featured_image && <img src={m.featured_image} alt={m.title} className="rounded-xl mb-3 h-36 w-full object-cover" />}
                <span className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{m.media_type}</span>
                <h4 className="text-lg text-[#1A242B] mt-1 flex items-center gap-2"><PlayCircle size={16} className="text-[#144F75] shrink-0" /> {m.title}</h4>
                {m.description && <RichContent preview text={m.description} className="text-sm text-[#4A5D6B] mt-2 line-clamp-2" />}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Photo gallery */}
      <GallerySection photos={e.gallery} />

      {!isVendor && centerSvcs.length > 0 && (
        <section className="mb-10" data-testid="storefront-center-services">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Book at my centers</h2>
          <p className="text-sm text-[#4A5D6B] mb-4">Sessions delivered by the centers I work with — pick any open time on the center's schedule.</p>
          {centerSvcs.map((g) => (
            <div key={g.location_id} className="mb-5" data-testid={`storefront-center-${g.location_id}`}>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-[#144F75] uppercase tracking-wide mb-3"><MapPin size={15} /> {g.location}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((s) => (
                  <div key={s.id} className="eyw-card p-5 flex flex-col" data-testid={`storefront-center-svc-${s.id}`}>
                    <h4 className="text-lg text-[#1A242B]">{s.title}</h4>
                    {s.description && <RichContent preview text={s.description} className="text-sm text-[#4A5D6B] mt-2 flex-1 line-clamp-3" />}
                    <div className="flex items-center justify-between mt-4">
                      <span className="font-serif text-xl text-[#144F75]">{s.price > 0 ? money(Number(s.price)) : "Complimentary"}</span>
                      <button onClick={() => setBooking(s)} data-testid={`storefront-center-book-${s.id}`} className="eyw-btn !py-2 !px-4 text-sm"><Calendar size={15} /> Book</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {!isVendor && (offerings?.length > 0 ? (
        <section className="mb-10" data-testid="storefront-offerings">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Offerings by location</h2>
          <div className="space-y-6">
            {offerings.map((g, gi) => (
              <div key={gi} data-testid={`offering-group-${gi}`}>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[#144F75] uppercase tracking-wide mb-3"><MapPin size={15} /> {g.location}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {g.items.map((s, si) => (
                    <div key={`${s.id}-${si}`} className="eyw-card p-5 flex flex-col" data-testid={`offering-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-lg text-[#1A242B]">{s.name}</h4>
                        {s.kind === "center" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72] shrink-0">Center</span>}
                      </div>
                      {s.reporting_category && <span className="text-xs text-[#8FA89B] mt-0.5">{s.reporting_category}</span>}
                      {(s.bio || s.description) && <RichContent text={s.bio || s.description} className="text-sm text-[#4A5D6B] mt-2 flex-1" />}
                      <div className="flex items-center justify-between mt-4">
                        <span className="font-serif text-xl text-[#144F75]">{s.price > 0 ? `${money(Number(s.price))}` : "Complimentary"}</span>
                        <div className="flex items-center gap-2">
                          <SocialShare path={`/enhancers/${e.slug || id}`} ogPath={`/api/share/offering/${id}/${s.service_id}`} title={`${s.name} — ${e.name}`} testid={`offering-share-${s.id}`} iconOnly />
                          <button onClick={() => setBooking({ id: s.service_id, title: s.name, price: Number(s.price || 0), member_discount_pct: 0, service_locations: [], _approval_id: s.kind === "center" ? s.id : "" })}
                            data-testid={`offering-book-${s.id}`} className="eyw-btn !py-2 !px-4 text-sm"><Calendar size={15} /> Book</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : e.services?.length > 0 && (
        <section className="mb-10" data-testid="storefront-services">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {e.services.map((s) => (
              <div key={s.id} className="eyw-card overflow-hidden flex flex-col" data-testid={`storefront-service-${s.id}`}>
                <CatalogImage src={s.image} alt={s.title} testid={`storefront-service-image-${s.id}`} />
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-lg text-[#1A242B]">{s.title}</h3>
                  <p className="text-sm text-[#4A5D6B] mt-1 flex-1">{s.short_description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-serif text-xl text-[#144F75]">{money(s.price)}</span>
                    <button onClick={() => setBooking(s)} data-testid={`storefront-book-${s.id}`} className="eyw-btn !py-2 !px-4 text-sm"><Calendar size={15} /> Book</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {e.products?.length > 0 && (
        <section className="mb-10" data-testid="storefront-products">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Products</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {e.products.map((p) => (
              <div key={p.id} className="eyw-card overflow-hidden flex flex-col" data-testid={`storefront-product-${p.id}`}>
                <CatalogImage src={p.image} alt={p.title} testid={`storefront-product-image-${p.id}`} />
                <div className="p-5 flex flex-col flex-1">
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#144F75] mb-1">{p.type === "digital" ? <Download size={12} /> : <Package size={12} />} {p.type}</span>
                  <h3 className="text-lg text-[#1A242B]">{p.title}</h3>
                  <p className="text-sm text-[#4A5D6B] mt-1 flex-1">{p.short_description}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-serif text-xl text-[#144F75]">{money(p.price)}</span>
                    <button onClick={() => add(p, 1)} data-testid={`storefront-add-${p.id}`} className="eyw-btn !py-2 !px-4 text-sm"><ShoppingBag size={15} /> Add</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!isVendor && <StorefrontPackages packages={e.packages} title={`${e.name.split(" ")[0]}'s session packages`} />}

      {!isVendor && hasCenterOfferings && curatedPackages.length > 0 && (
        <StorefrontPackages packages={curatedPackages} title="Wellness packages — buy for yourself or gift a friend" />
      )}

      {e.locations?.length > 0 && (
        <section className="mb-10" data-testid="storefront-locations">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Where to find {e.name.split(" ")[0]}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {e.locations.map((l) => (
              <div key={l.id} className="eyw-card p-4 flex items-start gap-3">
                <MapPin size={18} className="text-[#D98E73] mt-0.5" />
                <div><p className="font-semibold text-[#1A242B]">{l.name}</p><p className="text-sm text-[#4A5D6B]">{[l.city, l.state].filter(Boolean).join(", ") || l.type}</p></div>
              </div>
            ))}
          </div>
        </section>
      )}

      <StorefrontArticles enhancerId={id} />

      {(e.reviews.length > 0 || e.can_review || isStaff) && (
      <section data-testid="storefront-reviews">
        <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Reviews</h2>
        {(e.can_review) && <div className="mb-6"><ReviewComposer enhancerId={id} isStaff={isStaff} /></div>}
        {e.reviews.length === 0 ? (
          <p className="text-[#4A5D6B] py-6">No reviews yet — be the first to share your experience.</p>
        ) : (
          <div className="space-y-4">
            {e.reviews.map((r) => (
              <div key={r.id} className="eyw-card p-5" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {r.featured && <MessageSquareQuote size={16} className="text-[#D98E73]" />}
                    <span className="font-semibold text-[#1A242B]">{r.author_name || "Member"}</span>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {(isStaff || r.author_id === user?.id) && (
                    <button onClick={() => delReview.mutate(r.id)} data-testid={`review-del-${r.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  )}
                </div>
                {r.comment && <p className="text-[#4A5D6B] mt-2 leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
      )}
      <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="enhancer-gift-cards">
        <div className="max-w-2xl"><GiftCardPurchase enhancerId={e?.id || ""} title={`Gift a session with ${e?.name?.split(" ")[0] || "this enhancer"}`} testid="giftcert-card" /></div>
      </section>

      {booking && <BookingModal service={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}
